/**
 * useChildPlaybookMessages - Hook to fetch and merge messages from child playbook sessions.
 *
 * When a playbook runs executePlaybook, it creates a child ChatSession. This hook:
 * 1. Fetches all playbook runs using rootChatSessionId index (O(1) query)
 * 2. Extracts unique chatSessionIds from those runs
 * 3. Fetches messages from each child session
 * 4. Merges with root session messages, sorted by createdAt
 * 5. Refreshes durable child content in the background on tab-return, so an
 *    elicit/output persisted while the tab was hidden is recovered without the
 *    consumer having to remount (see B-1341 / B-1345). Any consumer that feeds
 *    the merged messages into `ChatProvider` gets this recovery for free.
 */
import { useApolloClient } from "@apollo/client";
import {
  type ChatMessageFragment,
  ChatSessionDocument,
  type ChatSessionQuery,
  type PlaybookRunFragment,
  usePlaybookRunsQuery,
} from "@qw/graphql";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useVisibilityRisingEdge } from "./use-visibility-rising-edge";

export interface UseChildPlaybookMessagesResult {
  messages: ChatMessageFragment[];
  playbookRuns: PlaybookRunFragment[];
  isLoading: boolean;
}

/**
 * Fetches messages from child playbook sessions and merges them with root session messages.
 *
 * @param rootSessionId - The root chat session ID
 * @param rootMessages - Messages from the root session
 * @returns Merged messages sorted by createdAt, all playbook runs, and loading state
 */
export function useChildPlaybookMessages(
  rootSessionId: string | undefined,
  rootMessages: ChatMessageFragment[]
): UseChildPlaybookMessagesResult {
  const apolloClient = useApolloClient();
  const [childMessages, setChildMessages] = useState<ChatMessageFragment[]>([]);
  const [fetchedSessionIds, setFetchedSessionIds] = useState<string[] | null>(null);

  // Monotonic token so an in-flight `refetch` can tell (at commit time) that its
  // result is stale. It advances on TWO events: (1) a conversation change,
  // bumped synchronously in the render that observes the switch — NOT a passive
  // effect — so a resolved-but-not-yet-committed continuation can't slip through
  // before the guard updates; and (2) the start of every `refetch()`, so among
  // overlapping same-session refreshes only the latest one commits (older, slower
  // responses can't overwrite newer state out of order).
  const refetchTokenRef = useRef(0);

  // Reset durable child state when the conversation changes, so switching
  // sessions shows the loading gate until the new tree loads rather than briefly
  // bleeding the previous conversation's child messages.
  const [prevRootSessionId, setPrevRootSessionId] = useState(rootSessionId);
  if (rootSessionId !== prevRootSessionId) {
    setPrevRootSessionId(rootSessionId);
    setChildMessages([]);
    setFetchedSessionIds(null);
    refetchTokenRef.current += 1;
  }

  const {
    data,
    loading: treeFetchLoading,
    refetch: refetchRuns,
  } = usePlaybookRunsQuery({
    variables: { chatSessionId: rootSessionId },
    skip: !rootSessionId,
  });
  const allPlaybookRuns = data?.playbookRuns ?? [];
  const treeFetchComplete = !treeFetchLoading;

  const childSessionIds = useMemo(() => {
    if (!rootSessionId || allPlaybookRuns.length === 0 || !treeFetchComplete) {
      return [];
    }

    const sessionIds = new Set<string>();

    for (const run of allPlaybookRuns) {
      if (run.chatSessionId && run.chatSessionId !== rootSessionId) {
        sessionIds.add(run.chatSessionId);
      }
    }

    return [...sessionIds];
  }, [rootSessionId, allPlaybookRuns, treeFetchComplete]);

  // Network-only fetch of every child session's messages. Shared by the
  // mount-time effect and the imperative `refetch` below so both stay in sync.
  const loadChildMessages = useCallback(
    async (sessionIds: string[]): Promise<ChatMessageFragment[]> => {
      if (sessionIds.length === 0) {
        return [];
      }

      const results = await Promise.all(
        sessionIds.map((sessionId) =>
          apolloClient.query<ChatSessionQuery>({
            query: ChatSessionDocument,
            variables: { id: sessionId },
            fetchPolicy: "network-only",
          })
        )
      );

      return results.flatMap((result) => result.data?.chatSession?.messages ?? []);
    },
    [apolloClient]
  );

  useEffect(() => {
    if (!treeFetchComplete) {
      return;
    }

    const sessionIdsKey = childSessionIds.join(",");
    const fetchedKey = fetchedSessionIds?.join(",");
    if (sessionIdsKey === fetchedKey) {
      return;
    }

    let cancelled = false;

    const fetchChildMessages = async () => {
      const allMessages = await loadChildMessages(childSessionIds);
      if (cancelled) {
        return;
      }
      setChildMessages(allMessages);
      setFetchedSessionIds(childSessionIds);
    };

    void fetchChildMessages();

    return () => {
      cancelled = true;
    };
  }, [childSessionIds, treeFetchComplete, fetchedSessionIds, loadChildMessages]);

  // Background refresh of the run tree + child-session messages, even when the
  // child session id set is unchanged. Deliberately does NOT reset
  // `fetchedSessionIds` up front, so `isLoading` stays false and the consumer's
  // subtree is never unmounted — the previous `childMessages` stay rendered
  // until the replacement set is ready.
  const refetch = useCallback(async () => {
    if (!rootSessionId) {
      return;
    }

    // Advance per-request so only the latest overlapping refetch commits.
    refetchTokenRef.current += 1;
    const requestToken = refetchTokenRef.current;

    const runsResult = await refetchRuns();
    const runs = runsResult.data?.playbookRuns ?? [];
    const sessionIds = [
      ...new Set(
        runs
          .filter((run) => Boolean(run.chatSessionId) && run.chatSessionId !== rootSessionId)
          .map((run) => run.chatSessionId as string)
      ),
    ];

    const messages = await loadChildMessages(sessionIds);

    // Discard a result that resolved after the conversation changed — otherwise a
    // late refetch for the previous session bleeds its child messages into the new
    // one (the mount effect has the equivalent `cancelled` guard). The token is
    // bumped synchronously on the switch, so this closes the microtask-vs-effect
    // window a passive-effect ref mirror would leave open.
    if (refetchTokenRef.current !== requestToken) {
      return;
    }

    setChildMessages(messages);
    setFetchedSessionIds(sessionIds);
  }, [rootSessionId, refetchRuns, loadChildMessages]);

  // Recover durable child content on tab-return. While hidden, a persisted child
  // elicit/output is surfaced by neither the ephemeral token stream nor the
  // mount-only fetch. Fire on EVERY hidden→visible rising edge (not only when
  // child sessions already exist), so `refetchRuns()` can discover a child
  // session *created* while the tab was hidden — the primary "launch playbook,
  // tab away, elicit appears while gone" flow. `refetch` no-ops without a
  // `rootSessionId`, and does a single indexed run-tree query when there are no
  // children, so an unconditional probe is cheap for plain chats too.
  useVisibilityRisingEdge(() => {
    if (rootSessionId) {
      void refetch();
    }
  });

  // Gates the consumer's loading spinner. True only until the FIRST child-message
  // load settles (or after a conversation switch resets the state). Background
  // refreshes keep `fetchedSessionIds` non-null, so they never flip this true —
  // which is what keeps a tab-return refresh from unmounting the chat subtree.
  const isLoading = fetchedSessionIds === null;

  const mergedMessages = useMemo(() => {
    const dedupedChildMessages = childMessages.filter((message) => message.role !== "user");
    const allMessages = [...rootMessages, ...dedupedChildMessages];
    return allMessages.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  }, [rootMessages, childMessages]);

  return { messages: mergedMessages, playbookRuns: allPlaybookRuns, isLoading };
}
